import React, { useState, useEffect, useRef } from 'react';
import { PRODUCTS } from '../data/siteData';
import { useCountdown } from '../hooks/useCountdown';

function CountdownStrip({ targetDate }) {
  const { days, hours, minutes, seconds } = useCountdown(targetDate);
  return (
    <div className="countdown-strip" aria-label="Countdown to launch">
      <span className="cd-label">Launches in</span>
      {[
        { n: days,    l: 'Days' },
        { n: hours,   l: 'Hrs' },
        { n: minutes, l: 'Min' },
        { n: seconds, l: 'Sec' },
      ].map((u, i) => (
        <React.Fragment key={u.l}>
          {i > 0 && <span className="cd-sep">:</span>}
          <div className="cd-unit">
            <span className="cd-n">{u.n}</span>
            <span className="cd-l">{u.l}</span>
          </div>
        </React.Fragment>
      ))}
      <span className="cd-date">📅 Sep 28, 2026</span>
    </div>
  );
}

function ProductPanel({ product, active }) {
  const panelRef = useRef(null);

  return (
    <div
      className={`ppanel${active ? ' on' : ''}`}
      id={`pp-${product.id}`}
      aria-hidden={!active}
      ref={panelRef}
    >
      <div className="pp-left">
        {/* Release badge */}
        <div className={`rel-badge ${product.launching ? 'rel-launching' : 'rel-soon'}`}
          style={!product.launching ? { borderColor: 'rgba(245,158,11,.35)', color: '#B45309' } : {}}>
          {product.launchLabel}
        </div>

        {/* Platform badge */}
        <div className="pp-badge" style={{
          background: product.badgeColor,
          border: `1.5px solid ${product.badgeBorder}`,
          color: product.badgeText
        }}>
          {product.emoji} {product.badge}
        </div>

        <h3>
          {product.name}<br />
          {product.subtitle}
        </h3>

        {/* Countdown for DevBrains */}
        {product.launchDate && <CountdownStrip targetDate={product.launchDate} />}

        <p>{product.description}</p>

        <div className="pp-feats">
          {product.features.map((f) => (
            <div className="ppf" key={f.text}>
              <div className="ppf-ic" style={{ background: product.badgeColor }}>{f.icon}</div>
              {f.text}
            </div>
          ))}
        </div>

        <div className="pp-actions">
          <a
            href={product.ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className={product.launching ? 'btn btn-green' : 'notify-btn'}
          >
            {product.ctaLabel}
          </a>
          <a
            href="https://wa.me/918123629993"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-g"
          >
            💬 Ask Us
          </a>
        </div>
      </div>

      {/* Right: mock UI preview */}
      <div className="pp-right">
        <div className="pvc">
          <div className="pvc-bar">
            <div className="pvc-dot" style={{ background: '#ff5f57' }} />
            <div className="pvc-dot" style={{ background: '#febc2e' }} />
            <div className="pvc-dot" style={{ background: '#28c840' }} />
            <div className="pvc-title">{product.name} — Preview</div>
          </div>
          <div className="pvc-body">
            <MockPreview product={product} />
          </div>
        </div>
      </div>
    </div>
  );
}

function MockPreview({ product }) {
  if (product.id === 'devbrains') {
    return (
      <div className="mock-devbrains">
        <div className="mock-mentor-list">
          {[
            { name: 'Aarav Kumar', role: 'Senior SDE · Microsoft', score: '98', color: '#00B14F' },
            { name: 'Priya Nair', role: 'ML Engineer · Google', score: '96', color: '#3B82F6' },
            { name: 'Vikram Rao', role: 'Cloud Arch · AWS', score: '94', color: '#7C3AED' },
          ].map((m) => (
            <div className="mock-mentor" key={m.name}>
              <div className="mock-av" style={{ background: m.color }}>{m.name[0]}</div>
              <div>
                <div className="mock-name">{m.name}</div>
                <div className="mock-role">{m.role}</div>
              </div>
              <div className="mock-score" style={{ color: m.color }}>⭐ {m.score}</div>
            </div>
          ))}
        </div>
        <div className="mock-match-btn">🤖 AI Match Me Now</div>
      </div>
    );
  }
  if (product.id === 'buyandsell') {
    return (
      <div className="mock-marketplace">
        <div className="mock-search">🔍 Search items near you…</div>
        <div className="mock-grid">
          {[
            { name: 'iPhone 15 Pro', price: '₹78,000', badge: '✅ Verified', color: '#00B14F' },
            { name: 'MacBook Air M2', price: '₹89,500', badge: 'AI Price ✓', color: '#3B82F6' },
            { name: 'Sony WH-1000XM5', price: '₹24,999', badge: '🔥 Hot Deal', color: '#EF4444' },
            { name: 'DJI Mini 3', price: '₹42,000', badge: 'New Arrival', color: '#7C3AED' },
          ].map((item) => (
            <div className="mock-item" key={item.name}>
              <div className="mock-item-img">📦</div>
              <div className="mock-item-name">{item.name}</div>
              <div className="mock-item-price">{item.price}</div>
              <div className="mock-item-badge" style={{ color: item.color }}>{item.badge}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  // NeighbourPro
  return (
    <div className="mock-neighbour">
      <div className="mock-search">📍 Services near Thanjavur…</div>
      <div className="mock-services">
        {[
          { icon: '⚡', name: 'Electrician', rating: '4.9', time: '15 min away', color: '#F59E0B' },
          { icon: '🔧', name: 'Plumber', rating: '4.8', time: '8 min away', color: '#3B82F6' },
          { icon: '📚', name: 'Home Tutor', rating: '5.0', time: '20 min away', color: '#10B981' },
          { icon: '🏠', name: 'House Cleaner', rating: '4.7', time: '12 min away', color: '#8B5CF6' },
        ].map((s) => (
          <div className="mock-service" key={s.name}>
            <div className="mock-svc-ic" style={{ background: `${s.color}20`, color: s.color }}>{s.icon}</div>
            <div>
              <div className="mock-svc-name">{s.name}</div>
              <div className="mock-svc-meta">{s.time}</div>
            </div>
            <div className="mock-svc-rating">⭐ {s.rating}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Products() {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  const switchProd = (i) => {
    setActive(i);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % PRODUCTS.length);
    }, 7000);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % PRODUCTS.length);
    }, 7000);
    return () => clearInterval(timerRef.current);
  }, []);

  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('on'); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="products-sec" id="products" aria-labelledby="products-heading">
      <div className="wrap">
        <div className="sec-hd rv" ref={headerRef}>
          <div className="chip">🚀 Our Products</div>
          <h2 id="products-heading">Three Platforms. One Vision.</h2>
          <p>AI-powered platforms solving real challenges in mentorship, commerce, and community services.</p>
        </div>

        {/* Tab row */}
        <div className="prod-header-row" role="tablist" aria-label="Product tabs">
          {PRODUCTS.map((p, i) => (
            <button
              key={p.id}
              className={`ptab${active === i ? ' on' : ''}`}
              onClick={() => switchProd(i)}
              role="tab"
              aria-selected={active === i}
              aria-controls={`pp-${p.id}`}
            >
              {p.emoji} {p.tabLabel}
              <span style={{ fontSize: '.58rem', padding: '.1rem .38rem', borderRadius: '20px', marginLeft: '.25rem', fontWeight: 800, verticalAlign: 'middle', ...p.tabBadgeStyle }}>
                {p.tabBadge}
              </span>
            </button>
          ))}
        </div>

        {/* Panels */}
        <div style={{ position: 'relative' }}>
          {PRODUCTS.map((p, i) => (
            <ProductPanel key={p.id} product={p} active={active === i} />
          ))}
        </div>
      </div>
    </section>
  );
}
